import type { CustomerJobMetadataPolicy } from '~/lib/customer-job-metadata';
import { TwtPhotoType } from '~/tenants/twt/performable/photo/TwtPhotoConfig';
import { TwtEditingType } from '~/tenants/twt/performable/editing/TwtEditingConfig';

/**
 * Twist (and twt2, bmd, which share its services): which fields of each
 * service a customer's post may set (lib/customer-job-metadata.ts), by service
 * id. Every field of every service's schema is listed once, customer or office
 * (customer-job-metadata.test.ts).
 */
export const TWT_CUSTOMER_JOB_METADATA: Record<string, CustomerJobMetadataPolicy> = {
  // TwtPhotoForm/TwtPhotosTwilight show the office alone the sunrise shoot, the photo count override (it
  // sets the Daytime Shoot price, TwtPhotoJob.calculate) and the relicensing fee; Interior Only, Reshoot
  // (Exterior Only) and 5 photos; no form sets `neighborhood`.
  photo: {
    customer: [
      'type',
      'twilight',
      'instructions',
      'libraries',
      'neighborhood_custom',
      'highlights',
      'domain',
      'photos',
      'minutes',
    ],
    office: ['sunrise', 'license', 'photos_override', 'neighborhood'],
    officeValues: { type: [TwtPhotoType.INTERIOR_ONLY, TwtPhotoType.RESHOOT_EXTERIOR], photos: [-5] },
  },
  aerial: {
    customer: ['type', 'boundaries_photo', 'boundaries_video', 'instructions', 'survey', 'pin_text'],
    office: [],
  },
  twilight: { customer: ['instructions', 'type'], office: [] },
  // no form: the tour links and the Nucleus switch are the office's.
  nucleus: {
    customer: ['guided', 'hotspots', 'detached', 'videos'],
    office: ['nucleus', 'branded_url', 'unbranded_url'],
  },
  'nucleus-guided': { customer: ['guided'], office: [] },
  'nucleus-hotspots': { customer: ['hotspots', 'instructions'], office: [] },
  // no form: the tour links and the Nucleus switch (which changes the deliverable) are the office's.
  '3d': { customer: ['floors', 'detached'], office: ['nucleus', 'branded_url', 'unbranded_url'] },
  // TwtVideoForm shows Raw Footage to the office only (the Jonny package fixes it).
  video: { customer: ['voiceover', 'virtual'], office: ['raw'] },
  'neighborhood-amenity-video': { customer: [], office: [] },
  floorplan: { customer: ['upgrade', 'detached'], office: [] },
  // TwtReelForm shows Raw Footage to the office only.
  reel: { customer: ['voiceover'], office: ['raw'] },
  'ai-reel': { customer: [], office: [] },
  slideshow: { customer: [], office: [] },
  'social-vertical': { customer: [], office: [] },
  // TwtEditingAction offers every edit but Blue Sky ($0) and Sunset Sky (TWT_EDITING_OPTIONS).
  editing: {
    customer: ['images'],
    office: [],
    officeValues: { 'images[].type': [TwtEditingType.BLUE_SKY, TwtEditingType.SUNSET_SKY] },
  },
  virtual: { customer: ['sky', 'instructions', 'photos'], office: [] },
  staging: { customer: ['photos'], office: [] },
  zillow: { customer: [], office: [] },
  'matterport-video': { customer: [], office: [] },
  'nucleus-video': { customer: [], office: [] },
  byop: { customer: ['domain'], office: [] },
};
